import { useState, useRef } from 'react';
import { adminService } from '../services/adminService';

export const useCustomerActions = (customers, setCustomers, updateLocalCustomer) => {
    const [approvalModalOpen, setApprovalModalOpen] = useState(false);
    const [customerToApprove, setCustomerToApprove] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [customerToReject, setCustomerToReject] = useState(null);

    // Action States
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectionLoading, setRejectionLoading] = useState(false);
    const [approvalNotes, setApprovalNotes] = useState('');
    const [serviceActivationDate, setServiceActivationDate] = useState('');
    const [vocusServiceId, setVocusServiceId] = useState('');
    const [avcId, setAvcId] = useState('');
    const [rejectionNotes, setRejectionNotes] = useState('');
    const [rejectionSendEmail, setRejectionSendEmail] = useState(true);

    // Synchronous lock against rapid double-clicks on Approve. The disabled
    // attribute on the button can't help: a click within ~16ms of the first
    // click fires before React re-renders, so both onClicks reach this
    // function. The lambda's auto-charge then runs Stripe twice. A ref check
    // is synchronous and rejects the second invocation immediately.
    const approveInFlightRef = useRef(false);
    const rejectInFlightRef = useRef(false);

    // Open Modals
    const openApprovalModal = (customer) => {
        setCustomerToApprove(customer);
        setApprovalNotes('');
        setServiceActivationDate(new Date().toISOString().split('T')[0]);
        // Pre-fill from existing customer record so a re-approval flow doesn't
        // wipe identifiers already captured.
        setAvcId(customer?.avcId || '');
        setVocusServiceId(customer?.vocusOrderSnapshot?.serviceId || '');
        setApprovalModalOpen(true);
    };

    // Approval Logic
    const approveCustomer = async () => {
        if (!customerToApprove) return;

        // Synchronous double-click guard. Runs before any awaits, before React
        // re-renders the disabled state. A rapid second click will hit this
        // branch and be ignored — preventing the duplicate Stripe charge that
        // happened previously when the disabled-button protection alone wasn't
        // enough.
        if (approveInFlightRef.current) {
            console.warn('[approveCustomer] ignored duplicate click — approval already in flight');
            return;
        }
        approveInFlightRef.current = true;

        setActionLoading(true);
        try {
            const result = await adminService.approveCustomer({
                customerId: customerToApprove.UID,
                customerEmail: customerToApprove.email,
                approvalNotes,
                serviceActivationDate,
                avcId: avcId?.trim() || null,
                vocusServiceId: vocusServiceId?.trim() || null,
                approvedBy: 'admin-portal' // TODO: Get actual user
            });

            if (result.success) {
                if (result.customer) updateLocalCustomer(result.customer);
                setApprovalModalOpen(false);
                setCustomerToApprove(null);
                // Differentiate "approved + charged" from "approved but charge
                // skipped because a paid pro-rated invoice already exists".
                // The second case is the Retry Initial Charge guard rail.
                if (result.autoCharge?.skipped && result.autoCharge?.reason === 'already_charged') {
                    alert(
                        `Customer already has a paid pro-rated invoice (${result.autoCharge.paidInvoiceNumber}` +
                        `, $${Number(result.autoCharge.paidAmount).toFixed(2)}).\n\n` +
                        `No new charge issued — the initial charge has already succeeded.\n\n` +
                        `If you need to re-charge for a different reason, use Manual Adjustments → Issue Refund/Credit Note.`
                    );
                } else if (result.autoCharge?.success === false) {
                    alert(`Customer approved, but initial charge FAILED: ${result.autoCharge.error || 'unknown error'}`);
                } else {
                    alert('Customer approved successfully!');
                }
            } else {
                throw new Error(result.error || 'Failed to approve');
            }
        } catch (error) {
            console.error(error);
            alert(`Error: ${error.message}`);
        } finally {
            setActionLoading(false);
            approveInFlightRef.current = false;
        }
    };

    // Cancellation flow lives in the Cancel Requests tab — see CancelRequestsManagement.jsx
    // and lambda-customer-cancel-request.js. The old inline cancelCustomer here called
    // the half-implemented handleCancelService that left orphaned Cognito/Vocus state.

    // Rejection — opens the styled RejectModal (was a native window.prompt
    // which captured no checkbox state and looked terrible).
    const openRejectModal = (customer) => {
        setCustomerToReject(customer);
        setRejectionNotes('');
        setRejectionSendEmail(true);
        setRejectModalOpen(true);
    };

    const rejectCustomer = async () => {
        if (!customerToReject) return;
        if (rejectInFlightRef.current) {
            console.warn('[rejectCustomer] ignored duplicate click — rejection already in flight');
            return;
        }
        rejectInFlightRef.current = true;
        setRejectionLoading(true);
        try {
            const result = await adminService.rejectCustomer({
                customerId: customerToReject.UID,
                customerEmail: customerToReject.email,
                rejectionNotes: rejectionNotes.trim(),
                sendEmail: rejectionSendEmail,
                adminUser: 'admin-portal',
            });
            if (result.success) {
                if (result.customer) updateLocalCustomer(result.customer);
                setRejectModalOpen(false);
                setCustomerToReject(null);
                alert(`Customer rejected.${rejectionSendEmail ? ' Rejection email sent.' : ''}`);
            } else {
                throw new Error(result.error || 'Failed to reject');
            }
        } catch (error) {
            console.error(error);
            alert(`Error: ${error.message}`);
        } finally {
            setRejectionLoading(false);
            rejectInFlightRef.current = false;
        }
    };

    // Immediate Status Updates (Reject, Hold, etc)
    const updateStatus = async (customerOrId, newStatus) => {
        let customer = typeof customerOrId === 'object' ? customerOrId : customers.find(c => c.UID === customerOrId);
        if (!customer) return;

        if (newStatus === 'approved') return openApprovalModal(customer);
        if (newStatus === 'rejected') return openRejectModal(customer);
        if (newStatus === 'cancelled') {
            alert('Use the Cancel Requests tab to cancel a customer service. The new flow handles Cognito + Vocus + invoice cleanup.');
            return;
        }

        if (newStatus === 'on_hold') {
            const notes = prompt('Hold notes:');
            if (!notes) return;
            await handleSimpleAction(adminService.putOnHold, {
                customerId: customer.UID,
                customerEmail: customer.email,
                holdNotes: notes
            });
        }
    };

    const handleSimpleAction = async (apiMethod, payload) => {
        try {
            const result = await apiMethod(payload);
            if (result.success && result.customer) {
                updateLocalCustomer(result.customer);
                alert('Status updated successfully.');
            } else {
                throw new Error(result.error);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    return {
        approvalModalOpen,
        setApprovalModalOpen,
        customerToApprove,
        approveCustomer,
        approvalLoading: actionLoading,
        approvalNotes,
        setApprovalNotes,
        serviceActivationDate,
        setServiceActivationDate,
        vocusServiceId,
        setVocusServiceId,
        avcId,
        setAvcId,

        // Reject flow
        rejectModalOpen,
        setRejectModalOpen,
        customerToReject,
        rejectCustomer,
        rejectionLoading,
        rejectionNotes,
        setRejectionNotes,
        rejectionSendEmail,
        setRejectionSendEmail,

        updateStatus
    };
};
